import { booleanAttribute, Component, EventEmitter, forwardRef, Input, numberAttribute, Output } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { getRichTextLength } from 'src/app/shared/helpers/rich-text';
import { UntilDestroy } from '@ngneat/until-destroy';
import { EditorModule } from '@progress/kendo-angular-editor';
import { SVGIcon } from '@progress/kendo-angular-icons';
import { applyFormatIcon, clearCssIcon } from '@progress/kendo-svg-icons';

const CUSTOM_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => RichEditorComponent),
    multi: true
};

@UntilDestroy()
@Component({
    selector: 'nova-rich-editor',
    providers: [CUSTOM_VALUE_ACCESSOR],
    template: `<kendo-editor
            #editor
            [iframe]="useIframe"
            [(ngModel)]="value"
            [disabled]="isDisabled"
            [pasteCleanupSettings]="pasteCleanupSettings"
            class="kendo-editor__border"
            [class.kendo-editor__border--error]="errorMessage"
            (blur)="onBlur()">
            <kendo-toolbar [overflow]="true">
                <kendo-toolbar-buttongroup>
                    <kendo-toolbar-button kendoEditorBoldButton></kendo-toolbar-button>
                    <kendo-toolbar-button kendoEditorItalicButton></kendo-toolbar-button>
                    <kendo-toolbar-button kendoEditorUnderlineButton></kendo-toolbar-button>
                    <kendo-toolbar-button kendoEditorInsertUnorderedListButton></kendo-toolbar-button>
                    <kendo-toolbar-button kendoEditorInsertOrderedListButton></kendo-toolbar-button>
                    @if (displayIndentControls) {
                        <kendo-toolbar-button kendoEditorIndentButton></kendo-toolbar-button>
                        <kendo-toolbar-button kendoEditorOutdentButton></kendo-toolbar-button>
                    }
                </kendo-toolbar-buttongroup>

                @if (displayLinkControls) {
                    <kendo-toolbar-buttongroup>
                        <kendo-toolbar-button kendoEditorCreateLinkButton></kendo-toolbar-button>
                        <kendo-toolbar-button kendoEditorUnlinkButton></kendo-toolbar-button>
                    </kendo-toolbar-buttongroup>
                }

                <kendo-toolbar-buttongroup>
                    <kendo-toolbar-button
                        title="Highlight"
                        [svgIcon]="applyFormatIcon"
                        (click)="editor.exec('backColor', 'yellow')"
                        [style]="{ display: enableTextHighlighting ? 'block' : 'none' }"></kendo-toolbar-button>
                    <kendo-toolbar-button
                        title="Remove highlight"
                        [svgIcon]="clearCss"
                        (click)="editor.exec('backColor', 'none')"
                        [style]="{ display: enableTextHighlighting ? 'block' : 'none' }"></kendo-toolbar-button>
                </kendo-toolbar-buttongroup>
            </kendo-toolbar>
        </kendo-editor>
        <span
            [hidden]="!errorMessage"
            class="kendo-editor__messages kendo-editor__messages--error"
            >{{ errorMessage }}</span
        >
        <span
            [hidden]="errorMessage || !maxlength"
            class="kendo-editor__messages kendo-editor__messages--hint"
            >Character limit {{ maxlength }}</span
        >`,
    styles: [
        `
            ::ng-deep .k-editor .k-editor-content p {
                margin: 0;
            }
            ::ng-deep .k-editor .k-editor-content ul {
                margin: 0;
            }
            ::ng-deep .k-editor .k-editor-content ol {
                margin: 0;
            }
            .kendo-editor__border {
                border-radius: 5px;
            }
            .kendo-editor__border--error {
                border: 2px solid #f44336;
            }
            .kendo-editor__messages {
                font-size: 75%;
                padding: 0 1em;
                margin-top: 0.67em;
                top: calc(100% - 1.8em);
            }
            .kendo-editor__messages--error {
                color: #f44336;
            }
            .kendo-editor__messages--hint {
                color: rgba(0, 0, 0, 0.6);
            }
        `
    ],
    standalone: true,
    imports: [EditorModule, FormsModule]
})

/**
 * These attributes and tags are restricted due to space (ie: style/class brings too much data over)
 * and/or cross site security concerns.
 * See:
 * https://www.acunetix.com/websitesecurity/cross-site-scripting/
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes
 */
export class RichEditorComponent implements ControlValueAccessor {
    clearCss: SVGIcon = clearCssIcon;
    applyFormatIcon: SVGIcon = applyFormatIcon;
    removeAttributes = ['class', 'href', 'src', 'srcset', 'style'];
    removeTags = [
        'a',
        'mark',
        'audio',
        'body',
        'div',
        'iframe',
        'input',
        'label',
        'link',
        'meta',
        'object',
        'path',
        'script',
        'span',
        'svg',
        'table',
        'td',
        'th',
        'video',
        // Angular Materials tags..
        'mat-checkbox'
    ];

    public pasteCleanupSettings = {
        convertMsLists: true,
        removeHtmlComments: true,
        removeAttributes: this.removeAttributes,
        stripTags: this.removeTags,
        // strip off MS Word classes & styles
        removeMsClasses: true,
        removeMsStyles: true,
        removeInvalidHTML: true
    };
    private text: any;

    dirty = false;
    errorMessage = '';

    @Output()
    valueChange = new EventEmitter();

    @Output()
    onValidityChange = new EventEmitter();

    private onChange = (value: any) => {
        this.text = value;
    };

    private onTouched = () => {};

    set value(text: any) {
        this.text = text;
        this.valueChange.emit(this.text);
        this.checkValidity();
        this.onChange(text);
    }

    /**
     *  1) useIframe=false means encapsulated mode (ie: iFrame is not used) and the editor will expand to fit its content.
     *  2) userIframe=true means encapsulated mode (ie: iFrame is used) and default height of 150px.
     */
    @Input({ transform: booleanAttribute })
    useIframe = true;

    @Input({ transform: booleanAttribute })
    isDisabled = false;

    @Input({ transform: numberAttribute })
    maxlength = 0;

    @Input()
    required = '';

    @Input({ transform: booleanAttribute })
    enableTextHighlighting = false;

    @Input({ transform: booleanAttribute })
    enableLinkButton = false;

    @Input({ transform: booleanAttribute })
    displayLinkControls = true;

    @Input({ transform: booleanAttribute })
    displayIndentControls = true;

    @Input()
    get value() {
        return this.text;
    }

    onBlur(): void {
        this.onTouched();
        this.dirty = true;
        this.checkValidity();
    }

    checkValidity(): void {
        if (this.dirty && this.required && !this.text?.replace('<p></p>', '')) {
            this.errorMessage = `${this.required} is required`;
        } else if (this.maxlength && getRichTextLength(this.text) > this.maxlength) {
            this.errorMessage = `Max length is ${this.maxlength}`;
        } else {
            this.errorMessage = '';
        }

        this.onValidityChange.emit(!this.errorMessage);
    }

    writeValue(obj: any): void {
        this.text = obj;
        this.checkValidity();
    }

    registerOnChange(fn: any) {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => {}): void {
        this.onTouched = fn;
    }
}
